import React, { useState, useEffect, useMemo } from "react"
import { Container, TextField, Typography, Box, Chip, Stack, Button } from "@mui/material"
import PeopleAltIcon from "@mui/icons-material/PeopleAlt"
import { onSnapshot, doc } from "firebase/firestore"
import { db } from "lib/firebase"
import PageWrapper from "../../PageWrapper"
import TempleTable from "./components/TempleTable"
import RelativeFilterDialog from "./components/RelativeFilterDialog"
import { useTempleRows, datasetStats, getRelatives } from "./hooks/useTempleRows"

const CACHE_KEY = "familyAddresses_cache"
const SELECTED_RELATIVES_KEY = "temples_selectedRelativeIds"

function loadSelectedIds() {
  try {
    const raw = localStorage.getItem(SELECTED_RELATIVES_KEY)
    if (!raw) return null
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? new Set(arr) : null
  } catch {
    return null
  }
}

function saveSelectedIds(set) {
  localStorage.setItem(SELECTED_RELATIVES_KEY, JSON.stringify([...set]))
}

function matchesSearch(row, q) {
  if (!q) return true
  const needle = q.toLowerCase()
  const fields = [
    row.templeName,
    row.templeLocation,
    row.aaAirport?.iata,
    row.aaAirport?.name,
    row.aaAirport?.city,
    row.zedAirport?.iata,
    row.zedAirport?.name,
    row.zedAirport?.city,
    row.relative?.name,
  ]
  return fields.some((f) => f && f.toLowerCase().includes(needle))
}

function TempleAirportTable() {
  const [familyAddresses, setFamilyAddresses] = useState(() => {
    const cached = localStorage.getItem(CACHE_KEY)
    return cached ? JSON.parse(cached) : []
  })
  const [search, setSearch] = useState("")
  const [maxRelativeMi, setMaxRelativeMi] = useState(50)
  const [selectedRelativeIds, setSelectedRelativeIds] = useState(loadSelectedIds)
  const [filterDialogOpen, setFilterDialogOpen] = useState(false)

  useEffect(() => {
    const ref = doc(db, "familyAddresses", "addresses")
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setFamilyAddresses(snap.data().data || [])
      }
    })
    return unsubscribe
  }, [])

  const allRelatives = useMemo(() => getRelatives(familyAddresses), [familyAddresses])

  // On first load with no saved selection, default to "everyone selected" so
  // the column behavior is unchanged for new users.
  useEffect(() => {
    if (selectedRelativeIds === null && allRelatives.length > 0) {
      setSelectedRelativeIds(new Set(allRelatives.map((r) => r.id)))
    }
  }, [allRelatives, selectedRelativeIds])

  const handleSelectionChange = (next) => {
    setSelectedRelativeIds(next)
    saveSelectedIds(next)
  }

  const rows = useTempleRows(familyAddresses, selectedRelativeIds)
  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (maxRelativeMi !== "" && (r.relativeMi == null || r.relativeMi > maxRelativeMi)) return false
        return matchesSearch(r, search)
      }),
    [rows, search, maxRelativeMi]
  )

  return (
    <PageWrapper title="Temples & Airports">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Temples & Airports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            For each operating LDS temple, the closest airports across two networks and the closest person from your address book.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Chip size="small" label={`${datasetStats.temples} temples`} />
          <Chip size="small" label={`${datasetStats.aa} AA airports`} />
          <Chip size="small" label={`${datasetStats.zed} ZED airports`} />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="Search — temple name, location, airport code, person name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="number"
            label="Max miles to closest person"
            value={maxRelativeMi}
            onChange={(e) => {
              const v = e.target.value
              setMaxRelativeMi(v === "" ? "" : Math.max(0, Number(v)))
            }}
            helperText="Blank = no limit"
            inputProps={{ min: 0, step: 25 }}
            sx={{ width: { xs: "100%", sm: 220 } }}
          />
          <Button
            variant="outlined"
            startIcon={<PeopleAltIcon />}
            onClick={() => setFilterDialogOpen(true)}
            sx={{ height: 40, whiteSpace: "nowrap" }}
            disabled={allRelatives.length === 0}
          >
            {selectedRelativeIds && selectedRelativeIds.size < allRelatives.length
              ? `People: ${selectedRelativeIds.size} of ${allRelatives.length}`
              : `People: All (${allRelatives.length})`}
          </Button>
        </Stack>

        <RelativeFilterDialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          relatives={allRelatives}
          selectedIds={selectedRelativeIds || new Set()}
          onChange={handleSelectionChange}
        />

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Showing {filtered.length} of {rows.length} temples. Click column headers to sort.
        </Typography>

        <TempleTable rows={filtered} />
      </Container>
    </PageWrapper>
  )
}

export default TempleAirportTable
